import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Recaptcha from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Modals from '../ducks/modals';
import { setUserDetails } from '../ducks/user';
import { ValidateField } from '../utils/ValidationHelper';
import {
  emitLoginEvent,
  emitLoginFailEvent,
  emitRegisterEvent,
  emitRegisterFailEvent,
} from '../utils/ct-events';
import { login, register } from '../utils/zoomServices';
import { Button } from './Button';
import Checkbox from './Checkbox';
import PicabooModal from './PicabooModal';
import { forgotPasswordBase } from '../config';
import { useSearchParams } from 'react-router-dom';
import { LoginModalConfig } from '../ducks/modals';
import { projectPKSelector } from '../reducers/selectors';

const MODE_LOGIN = 'LOGIN';
const MODE_REGISTER = 'REGISTER';

/**
 * @param {Object} props
 * @param {boolean} props.show
 * @param {import('zod').infer<typeof LoginModalConfig>} props.config
 */
export default function LoginModal({ show, config }) {
  const dispatch = useDispatch();
  const emailRef = useRef();
  const usernameRef = useRef();
  const passwordRef = useRef();
  const [error, setError] = useState(false);
  const [mode, setMode] = useState(MODE_LOGIN);
  const captchaRef = useRef(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const projectPK = useSelector(projectPKSelector);

  const { t } = useTranslation();

  const onSuccess = useMemo(() => {
    return () => {
      if (config.addToCart) {
        dispatch(Modals.showAddToCartModal());
      } else if (config.reload) {
        location.reload();
      } else {
        dispatch(Modals.hideModal());
      }
    };
  }, [config, dispatch]);

  const cleanseAndSetError = useCallback(
    (data) => {
      let errorData = data?.data?.message?.error;
      setError(Array.isArray(errorData) ? errorData : [errorData]);
    },
    [setError],
  );

  const handleLogin = useHandleLogin({
    emailRef,
    passwordRef,
    captchaRef,
    setIsLoggingIn,
    cleanseAndSetError,
    onSuccess,
  });

  const registerMe = useRegisterMe({
    usernameRef,
    emailRef,
    passwordRef,
    captchaRef,
    setIsLoggingIn,
    cleanseAndSetError,
    onSuccess,
  });

  return (
    <PicabooModal
      dialogStyle={'saveProgress'}
      show={show}
      onHide={() => {
        dispatch(Modals.hideModal());
      }}
    >
      <Modal.Header>
        <Modal.Title>
          {mode === MODE_LOGIN ? (
            <>
              {!!projectPK && (
                <h2>{t('LOGINMODAL.YOUR_CHANGES_ARE_NOT_SAVED')}</h2>
              )}
              <h2>{t('LOGINMODAL.Log_in_or_sign_up_to_save_your_project')}</h2>
            </>
          ) : (
            <h2>{t('LOGINMODAL.Sign_Up')}</h2>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="login">
          <div
            style={{ display: error ? '' : 'none' }}
            className="alertBox alertBox--error"
          >
            <div className="alertBox-column alertBox-icon">
              <icon glyph="ic-error" className="icon" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
                </svg>
              </icon>
              {error &&
                error.map((e, i) => {
                  return <div key={i}>{e.message}</div>;
                })}
            </div>
          </div>
          <div className="login-row">
            {mode == MODE_LOGIN ? (
              <LoginForm
                handleLogin={handleLogin}
                emailRef={emailRef}
                passwordRef={passwordRef}
                captchaRef={captchaRef}
                isLoggingIn={isLoggingIn}
                setMode={setMode}
              />
            ) : (
              <RegisterForm
                registerMe={registerMe}
                usernameRef={usernameRef}
                emailRef={emailRef}
                passwordRef={passwordRef}
                captchaRef={captchaRef}
                isLoggingIn={isLoggingIn}
                setMode={setMode}
              />
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer></Modal.Footer>
    </PicabooModal>
  );
}

function LoginForm({
  handleLogin,
  emailRef,
  passwordRef,
  captchaRef,
  isLoggingIn,
  setMode,
}) {
  const { t } = useTranslation();
  const termsUrl = 'https://www.blurb.com/terms';
  const privacyUrl = 'https://www.blurb.com/privacy';
  return (
    <div className="login-form form">
      <form onSubmit={handleLogin} noValidate={true}>
        <div className="required">{t('MODAL.Required')}</div>
        <div className="form-field form-field--input form-field--inputEmail">
          <div className="required-field">
            {t('LOGINMODAL.Email_or_Username')}
          </div>
          <input
            ref={emailRef}
            required={true}
            className="form-input"
            name="login_email"
            id="login_email"
            type="text"
            placeholder=""
            autoComplete={'username'}
          />
          <span className={'error'}></span>
        </div>

        <div className="form-field form-field--input form-field--inputPassword">
          <div className="required-field">{t('LOGINMODAL.Password')}</div>
          <input
            ref={passwordRef}
            required={true}
            className="form-input"
            id="login_pass"
            type="password"
            name="login_pass"
            autoComplete="current-password"
            placeholder=""
          />
          <span className={'error'}></span>
        </div>
        <span>
          <div className="form-field form-field--input">
            <Checkbox
              className="modalInputCheckbox"
              type="checkbox"
              id="optIn"
              name="optIn"
              text={t('LOGINMODAL.Keep_Me_Logged_In')}
            ></Checkbox>
          </div>
          <div className="forgot-password">
            <a href={forgotPasswordBase}>{t('LOGINMODAL.Forgot_password')}</a>
          </div>
        </span>
        <p className={'tc'}>
          {t('LOGINMODAL.TC_Privacy_1')}{' '}
          <a href={termsUrl} target="_blank">
            {t('LOGINMODAL.Terms_Conditions')}
          </a>{' '}
          {t('LOGINMODAL.TC_Privacy_2')}{' '}
          <a href={privacyUrl} target="_blank">
            {t('LOGINMODAL.Privacy_Policy')}
          </a>
        </p>
        <p className={'recaptcha'}>
          {t('LOGINMODAL.Recaptcha_1')}{' '}
          <a href={termsUrl} target="_blank">
            {t('LOGINMODAL.Terms_Conditions')}
          </a>{' '}
          {t('LOGINMODAL.Recaptcha_2')}{' '}
          <a href={privacyUrl} target="_blank">
            {t('LOGINMODAL.Privacy_Policy')}
          </a>
        </p>
        <RecaptchaControl
          captchaRef={captchaRef}
          checkRecaptchaState={checkRecaptchaState}
        />
        <div className="form-actions">
          <Button
            id="buttonLoginModalLogIn"
            width="182px"
            type="submit"
            variant="primary"
            loading={isLoggingIn}
          >
            {t('LOGINMODAL.Log_In')}
          </Button>
        </div>
        <div className="form-actions">
          {t('LOGINMODAL.Need_Blurb_Account')}&nbsp;
          <a href={'#'} onClick={() => setMode(MODE_REGISTER)}>
            {t('LOGINMODAL.Sign_Up')}
          </a>
        </div>
      </form>
    </div>
  );
}

function RegisterForm({
  registerMe,
  usernameRef,
  emailRef,
  passwordRef,
  captchaRef,
  isLoggingIn,
  setMode,
}) {
  const { t } = useTranslation();
  return (
    <div className="login-form form">
      <form onSubmit={registerMe} noValidate={true}>
        <div className="required">{t('MODAL.Required')}</div>
        <div className="form-field form-field--input form-field--inputText">
          <div className="required-field">{t('LOGINMODAL.Username')}</div>
          <input
            ref={usernameRef}
            required={true}
            className="form-input"
            name="register_username"
            id="register_username"
            type="text"
            placeholder=""
          />
          <span className={'error'}></span>
        </div>
        <div className="form-field form-field--input form-field--inputEmail">
          <div className="required-field">{t('LOGINMODAL.Email_Address')}</div>
          <input
            ref={emailRef}
            required={true}
            className="form-input"
            name="register_email"
            id="register_email"
            type="email"
            placeholder=""
          />
          <span className={'error'}></span>
        </div>
        <div className="form-field form-field--input form-field--inputPassword">
          <div className="required-field">Password</div>
          <input
            ref={passwordRef}
            required={true}
            className="form-input"
            id="register_pass"
            type="password"
            name="register_pass"
            autoComplete="off"
            placeholder=""
          />
          <span className={'error'}></span>
        </div>
        <div className="form-field form-field--input">
          <Checkbox
            className="modalInputCheckbox"
            type="checkbox"
            id="optIn"
            name="optIn"
            text={t('LOGINMODAL.Opt_In_Message')}
          ></Checkbox>
        </div>
        <p className={'tc'}>
          {t('LOGINMODAL.Create_Account_TC_Privacy_1')}{' '}
          <a href="#">{t('LOGINMODAL.Terms_Conditions')}</a>{' '}
          {t('LOGINMODAL.Create_Account_TC_Privacy_2')}{' '}
          <a href="#">{t('LOGINMODAL.Privacy_Policy')}</a>
        </p>
        <p className={'recaptcha'}>
          {t('LOGINMODAL.Recaptcha_1')}{' '}
          <a href="#">{t('LOGINMODAL.Terms_Conditions')}</a>{' '}
          {t('LOGINMODAL.Recaptcha_2')}{' '}
          <a href="#">{t('LOGINMODAL.Privacy_Policy')}</a>
        </p>
        <RecaptchaControl
          captchaRef={captchaRef}
          checkRecaptchaState={checkRecaptchaState}
        />
        <div className="form-actions">
          <Button
            id="buttonLoginModalRegister"
            width="182px"
            type="submit"
            variant="primary"
            loading={isLoggingIn}
          >
            {t('LOGINMODAL.Create_Account')}
          </Button>
        </div>
        <div className="form-actions">
          {t('LOGINMODAL.Have_Blurb_Account')}&nbsp;
          <a href={'#'} onClick={() => setMode(MODE_LOGIN)}>
            {t('LOGINMODAL.Log_In')}
          </a>
        </div>
      </form>
    </div>
  );
}

function useHandleLogin({
  emailRef,
  passwordRef,
  captchaRef,
  setIsLoggingIn,
  cleanseAndSetError,
  onSuccess,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bypassCaptchaToken = useBypassCaptchaToken();
  return useCallback(
    (e) => {
      e.preventDefault();

      const formElement = e.target;
      const isValid = formElement.checkValidity();

      formElement.classList.add('submitted');

      const firstInvalidField = formElement.querySelector(':invalid');
      firstInvalidField?.focus();
      let token = '';

      if (bypassCaptchaToken) {
        token = bypassCaptchaToken;
      } else {
        token = captchaRef.current.getValue();
        captchaRef.current.reset();
        if (!checkRecaptchaState(token)) {
          return;
        }
      }

      if (!isValid) {
        validateFields(formElement);
        return;
      }

      const email = emailRef.current.value;
      const password = passwordRef.current.value;
      setIsLoggingIn(true);
      (async () => {
        let data = await login(email, password, token);
        if (data?.data?.loggedIn === true) {
          emitLoginEvent(email);
          dispatch(setUserDetails(data.data));
          onSuccess();
        } else {
          emitLoginFailEvent(data?.data?.message?.error?.message, email);
          cleanseAndSetError(data);
        }
        setIsLoggingIn(false);
      })();
    },
    [
      captchaRef,
      emailRef,
      passwordRef,
      setIsLoggingIn,
      cleanseAndSetError,
      dispatch,
      t,
      bypassCaptchaToken,
    ],
  );
}

function useRegisterMe({
  usernameRef,
  emailRef,
  passwordRef,
  captchaRef,
  setIsLoggingIn,
  cleanseAndSetError,
  onSuccess,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bypassCaptchaToken = useBypassCaptchaToken();
  return useCallback(
    (e) => {
      e.preventDefault();

      const formElement = e.target;
      const isValid = formElement.checkValidity();

      formElement.classList.add('submitted');

      const email = emailRef.current.value;
      const password = passwordRef.current.value;
      const username = usernameRef.current.value;
      let token;
      if (bypassCaptchaToken) {
        token = bypassCaptchaToken;
      } else {
        token = captchaRef.current.getValue();
        captchaRef.current.reset();

        if (!checkRecaptchaState(token)) {
          return;
        }
      }

      if (!isValid) {
        validateFields(formElement);
        return;
      }
      setIsLoggingIn(true);
      (async () => {
        let data = await register(username, password, email, token);
        if (data?.data?.loggedIn === true) {
          emitRegisterEvent(email);
          dispatch(setUserDetails(data.data));
          onSuccess();
        } else {
          emitRegisterFailEvent(data?.data?.message?.error?.message, email);
          cleanseAndSetError(data);
        }
        setIsLoggingIn(false);
      })();
    },
    [
      usernameRef,
      emailRef,
      passwordRef,
      captchaRef,
      setIsLoggingIn,
      cleanseAndSetError,
      dispatch,
      t,
      bypassCaptchaToken,
    ],
  );
}

function validateFields(form) {
  const inputs = form.querySelectorAll('input:not([type=checkbox])');
  for (let i = 0; i < inputs.length; i++) {
    ValidateField(inputs[i]);
  }
}

function checkRecaptchaState(token) {
  const recaptchaError = document.querySelector('#recaptchaError');
  const isValid = token && token.length > 0;
  recaptchaError.className = isValid ? 'error' : 'error active';
  return isValid;
}

function RecaptchaControl({ captchaRef, checkRecaptchaState }) {
  const { t } = useTranslation();
  const hidden = useBypassCaptchaToken();
  if (hidden) {
    return null;
  }
  return (
    <div className={'recaptchaContainer'}>
      <Recaptcha
        sitekey={'6LfqXSEoAAAAAL2wdPFTaZdQ3C-EnoRzPm22X5bj'}
        ref={captchaRef}
        onChange={checkRecaptchaState}
      />
      <span className={'error'} id={'recaptchaError'}>
        {t('REGISTERMODAL.Recaptcha_requires_verification')}
      </span>
    </div>
  );
}

function useBypassCaptchaToken() {
  const [localStorageValue] = useState(
    () => localStorage.getItem('dev.bypassCaptchaToken') ?? null,
  );
  const [searchParams] = useSearchParams();
  const searchParamValue = searchParams.get('RECAPTCHA_BYPASS');
  return searchParamValue ?? localStorageValue;
}
